// frontend/src/components/ResetPasswordFromLink.jsx
// Password reset page for email link flow
//
// This component handles the password reset when user clicks the link in their email.
// Route: /reset-password?token=abc123
//
// Flow:
// 1. Extract token from URL query param
// 2. Validate token on mount (POST /validate-token)
// 3. If valid: show password reset form
// 4. If invalid/expired: show error with options
// 5. On success: redirect to login

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../axiosConfig';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import '../index.css';
import { validatePassword } from '../utils/passwordValidator';

function ResetPasswordFromLink() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null); // 'expired' or 'invalid'
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        setTokenError('invalid');
        setIsValidating(false);
        return;
      }

      try {
        const response = await axios.post(
          '/api/password-reset/validate-token',
          { token },
          { skipAuthSessionHandling: true }
        );

        if (response.data.valid) {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
          setTokenError(response.data.error || 'invalid');
        }
      } catch (error) {
        setIsTokenValid(false);
        setTokenError('invalid');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Handle password reset submission
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Validate password
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setErrorMessage(passwordValidation.message);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage(t('passwordsDoNotMatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        '/api/password-reset/complete',
        {
          resetToken: token,
          newPassword
        },
        { skipAuthSessionHandling: true }
      );

      if (response.data.success) {
        setIsSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      // Check if token has expired during the form filling
      if (error.response?.data?.error?.includes('expired')) {
        setIsTokenValid(false);
        setTokenError('expired');
      } else {
        setErrorMessage(error.response?.data?.error || t('passwordResetFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render loading state
  if (isValidating) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-gray-100 dark:bg-gray-800 shadow-md rounded-lg">
        <div className="flex items-center justify-center">
          <svg className="animate-spin h-8 w-8 text-blue-500 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600 dark:text-gray-400">
            {t('validatingResetLink')}
          </span>
        </div>
      </div>
    );
  }

  // Render error state (invalid or expired token)
  if (!isTokenValid) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-gray-100 dark:bg-gray-800 shadow-md rounded-lg">
        <div className="text-center">
          {/* Error icon */}
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-red-600 dark:text-red-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {tokenError === 'expired' ? t('resetLinkExpiredTitle') : t('resetLinkInvalidTitle')}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {tokenError === 'expired' ? t('resetLinkExpired') : t('resetLinkInvalid')}
          </p>

          <div className="space-y-3">
            <Link
              to="/forgot-password"
              className="block w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
            >
              {t('requestNewResetLink')}
            </Link>

            <Link
              to="/login"
              className="block w-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 transition duration-200"
            >
              {t('backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Render success state
  if (isSuccess) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-gray-100 dark:bg-gray-800 shadow-md rounded-lg">
        <div className="text-center">
          {/* Success icon */}
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-green-600 dark:text-green-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('passwordResetSuccessTitle')}
          </h2>

          <p className="text-gray-600 dark:text-gray-400">
            {t('passwordResetSuccessRedirect')}
          </p>
        </div>
      </div>
    );
  }

  // Render password reset form
  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-gray-100 dark:bg-gray-800 shadow-md rounded-lg">
      <h2 className="text-2xl font-bold mb-4 text-center text-blue-600 dark:text-blue-400">
        {t('resetPassword')}
      </h2>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
        {t('enterNewPassword')}
      </p>

      <form onSubmit={handlePasswordReset}>
        <div className="mb-4">
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('newPassword')}:
          </label>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            {t('passwordRequirementsHint')}
          </p>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              tabIndex="-1"
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('confirmPassword')}:
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? t('loading') : t('resetPassword')}
        </button>
      </form>

      {/* Error message */}
      {errorMessage && (
        <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded">
          {errorMessage}
        </div>
      )}

      {/* Back to login link */}
      <div className="mt-4 text-center">
        <Link
          to="/login"
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          {t('backToLogin')}
        </Link>
      </div>
    </div>
  );
}

export default ResetPasswordFromLink;
